import { ensureValidToken, isHttpStatusOk, notifyServerConnectionLost } from './Api_Functions'
import { getSessionValue } from './utilities/session_value_utils'
import { isWorkstationCovered, neverSettles, reportWorkstationCovered } from '../vision/visionSignals'

// The assistant gateway serves under /assistant, outside Sun's /endpoints/ tree.
// Calls are always same-origin: a proxy (in every environment) routes
// /assistant/* to the gateway keeping the prefix.
const assistantAddress = '/assistant'

// The health check must answer fast; anything slower counts as unavailable
const HEALTH_TIMEOUT_MS = 4000
// A chat turn can chain several tools and take up to ~1 minute with the local LLM
const CHAT_TIMEOUT_MS = 180000
// The stream sends a keep-alive comment every ~15 s, so a full minute of
// silence means the connection is dead, not that the LLM is thinking
const STREAM_INACTIVITY_TIMEOUT_MS = 60000

const buildHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: 'Bearer ' + window.localStorage.getItem('jwt'),
  'X-MIC-SESSION': getSessionValue()
})

// `confirm_action` only travels when the user has just confirmed a write;
// `charts` is the chat's chart selector (auto | never | always)
const buildBody = (message, history, language, confirmAction, charts) => {
  const body = { message, history, language }
  if (confirmAction) {
    body.confirm_action = confirmAction
  }
  if (charts) {
    body.charts = charts
  }
  return JSON.stringify(body)
}

const throwResponseError = async (response) => {
  const detail = await response.json().catch(() => ({}))
  const error = {}
  error.status = response.status
  error.timeout = false
  error.cancelled = false
  error.message = typeof detail.detail === 'string' ? detail.detail : (detail.message || '')
  // the gateway logs everything under this id: it is what makes a failure
  // reported from the plant traceable
  error.requestId = detail.request_id || ''
  throw error
}

// Sun rejected a tool with a 401: the JWT is no longer valid. The gateway
// signals it apart because the answer's text cannot be trusted to explain it
// (the model tends to suggest calling the administrator instead of re-login).
const throwSessionExpired = () => {
  notifyServerConnectionLost()
  const error = {}
  error.status = 401
  error.timeout = false
  error.cancelled = false
  error.sessionExpired = true
  error.message = 'Assistant session expired'
  throw error
}

const buildFetchError = (exception, callerSignal) => {
  const error = {}
  error.status = 0
  error.cancelled = Boolean(callerSignal && callerSignal.aborted)
  error.timeout = !error.cancelled && exception.name === 'AbortError'
  error.message = exception.message
  return error
}

/**
 * Health probe. The assistant is not deployed on every MIC and, without it,
 * the proxy fallback may answer 200 with the SPA's index.html — so callers
 * must validate `service === 'mic-assistant'` on the resolved body, not just
 * that this resolved.
 */
export const assistantHealth = async () => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS)
  try {
    const response = await fetch(assistantAddress + '/health', { signal: controller.signal })
    if (!response.ok) {
      const error = {}
      error.status = response.status
      error.message = 'Assistant gateway is not available'
      throw error
    }
    return await response.json()
  } finally {
    clearTimeout(timeoutId)
  }
}

// Parses one SSE block ("event: x\ndata: {...}"). Heartbeat comments (lines
// starting with ':') and incomplete blocks resolve to null.
const parseSseBlock = (block) => {
  let eventName = 'message'
  const dataLines = []
  for (const rawLine of block.split('\n')) {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine
    if (line.startsWith(':')) {
      continue
    }
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    }
  }
  if (dataLines.length === 0) {
    return null
  }
  try {
    return { event: eventName, data: JSON.parse(dataLines.join('\n')) }
  } catch (exception) {
    return null
  }
}

/**
 * Sends a chat turn to the assistant gateway over SSE and streams the answer.
 *
 * The conversation is stateless on the server: `history` must contain every
 * previous turn ({ role: 'user' | 'assistant', content }) in chronological
 * order, without the current message.
 *
 * - onToken(fragment) fires for every text fragment as it is generated.
 * - onTool({ name, status, error }) fires when a tool starts/ends executing;
 *   status 'pending' means a write is waiting for the user's confirmation.
 * - confirmAction (optional) is a `pending_action` the user just confirmed,
 *   passed back untouched: the gateway executes it without asking the LLM again.
 * - signal (optional) lets the caller cancel the generation; the gateway
 *   detects the disconnection and stops the LLM.
 *
 * Resolves to the `done` event payload ({ answer, tools_used, iterations,
 * truncated, pending_action, request_id }); `answer` is the definitive text
 * for the history.
 * Throws { status, message, timeout, cancelled, requestId }. A 401 before the stream
 * starts follows the same connection-lost flow as any Sun call; mid-stream
 * failures arrive as an SSE `error` event (the HTTP 200 is long gone) and are
 * thrown with status 502.
 */
export const assistantChatStream = async ({ message, history, language, confirmAction, charts, onToken = () => {}, onTool = () => {}, onChart = () => {}, signal }) => {
  await ensureValidToken()
  const controller = new AbortController()
  const abortFromCaller = () => controller.abort()
  if (signal) {
    signal.addEventListener('abort', abortFromCaller)
  }
  let inactivityTimer = setTimeout(() => controller.abort(), STREAM_INACTIVITY_TIMEOUT_MS)
  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer)
    inactivityTimer = setTimeout(() => controller.abort(), STREAM_INACTIVITY_TIMEOUT_MS)
  }
  try {
    let response
    try {
      response = await fetch(assistantAddress + '/chat/stream', {
        method: 'POST',
        headers: buildHeaders(),
        body: buildBody(message, history, language, confirmAction, charts),
        signal: controller.signal
      })
    } catch (exception) {
      throw buildFetchError(exception, signal)
    }

    if (response.status === 401) {
      if (notifyServerConnectionLost()) {
        return {}
      }
    }
    // Story 213 (C1 step 4): covered by camera vigilance; the guard takes over, as in Api_Functions.
    if (isWorkstationCovered(response.status)) {
      reportWorkstationCovered()
      return neverSettles()
    }
    if (!isHttpStatusOk(response.status)) {
      await throwResponseError(response)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let doneData = {}
    let finished = false

    while (!finished) {
      let chunk
      try {
        chunk = await reader.read()
      } catch (exception) {
        throw buildFetchError(exception, signal)
      }
      if (chunk.done) {
        break
      }
      resetInactivityTimer()
      buffer += decoder.decode(chunk.value, { stream: true })

      // SSE events are separated by a blank line; the last piece of the
      // buffer may be incomplete, so it waits for the next chunk.
      const blocks = buffer.split('\n\n')
      buffer = blocks.pop()

      for (const block of blocks) {
        const parsed = parseSseBlock(block)
        if (!parsed) {
          continue
        }
        if (parsed.event === 'token') {
          onToken(parsed.data.text || '')
        } else if (parsed.event === 'tool') {
          onTool(parsed.data)
        } else if (parsed.event === 'chart') {
          onChart(parsed.data)
        } else if (parsed.event === 'done') {
          doneData = parsed.data
          finished = true
        } else if (parsed.event === 'error') {
          const error = {}
          error.status = 502
          error.timeout = false
          error.cancelled = false
          error.message = parsed.data.detail || ''
          error.requestId = parsed.data.request_id || ''
          throw error
        }
      }
    }

    if (!finished) {
      // The connection ended without a done event: treat it as a failure
      const error = {}
      error.status = 0
      error.timeout = false
      error.cancelled = Boolean(signal && signal.aborted)
      error.message = 'Stream ended unexpectedly'
      throw error
    }
    if (doneData.session_expired) {
      throwSessionExpired()
    }
    return doneData
  } finally {
    clearTimeout(inactivityTimer)
    if (signal) {
      signal.removeEventListener('abort', abortFromCaller)
    }
  }
}

/**
 * Non-streaming variant (POST /assistant/chat): same request, whole answer at
 * once. Kept as a fallback and for diagnostics; resolves to the same payload
 * as the stream's `done` event.
 */
export const assistantChat = async (message, history, language, confirmAction, charts) => {
  await ensureValidToken()
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS)
  let response
  try {
    response = await fetch(assistantAddress + '/chat', {
      method: 'POST',
      headers: buildHeaders(),
      body: buildBody(message, history, language, confirmAction, charts),
      signal: controller.signal
    })
  } catch (exception) {
    throw buildFetchError(exception)
  } finally {
    clearTimeout(timeoutId)
  }

  if (response.status === 401) {
    if (notifyServerConnectionLost()) {
      return {}
    }
  }

  if (isWorkstationCovered(response.status)) {
    reportWorkstationCovered()
    return neverSettles()
  }

  if (!isHttpStatusOk(response.status)) {
    await throwResponseError(response)
  }

  const result = await response.json()
  if (result.session_expired) {
    throwSessionExpired()
  }
  return result
}
